import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Plus, Trash2 } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { ParameterCell } from './ParameterCell';
function mkParam(overrides) {
    return {
        label: overrides.name,
        paramType: 'quantity',
        source: 'contractor',
        value: null,
        perPeriod: false,
        ...overrides
    };
}
function InlineFteEditor({ value, onChange }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const handleStart = useCallback(()=>{
        setEditValue(value != null ? String(value) : '');
        setIsEditing(true);
    }, [
        value
    ]);
    const handleBlur = useCallback(()=>{
        setIsEditing(false);
        if (editValue === '') {
            onChange(undefined);
            return;
        }
        const parsed = Number(editValue);
        if (Number.isNaN(parsed) || parsed < 0) return;
        if (parsed !== value) onChange(parsed);
    }, [
        editValue,
        value,
        onChange
    ]);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') e.target.blur();
        else if (e.key === 'Escape') setIsEditing(false);
    }, []);
    if (isEditing) {
        return /*#__PURE__*/ _jsx("input", {
            type: "number",
            value: editValue,
            onChange: (e)=>setEditValue(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            className: "w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right",
            step: "any",
            min: "0"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "px-2 py-1.5 text-sm text-right rounded cursor-pointer hover:bg-gray-50",
        onClick: handleStart,
        children: value != null ? value : '—'
    });
}
export const SharedTierEditor = /*#__PURE__*/ memo(function SharedTierEditor({ config, rateCard, columns, referencingClins, allSharedTierConfigs, onChange }) {
    const [showAddRole, setShowAddRole] = useState(false);
    const [showCopyFrom, setShowCopyFrom] = useState(false);
    const group = config.group;
    const lines = group.lines;
    const assignedIds = useMemo(()=>new Set(lines.map((l)=>l.laborCategoryId).filter(Boolean)), [
        lines
    ]);
    const availableCategories = useMemo(()=>{
        if (!rateCard) return [];
        return rateCard.categories.filter((cat)=>!assignedIds.has(cat.laborCategoryId));
    }, [
        rateCard,
        assignedIds
    ]);
    const currentFtes = useMemo(()=>lines.reduce((sum, l)=>sum + (l.fteCount ?? 0), 0), [
        lines
    ]);
    const { min: minFtes, max: maxFtes } = config.fteConstraints;
    const fteWarning = minFtes != null && currentFtes < minFtes || maxFtes != null && currentFtes > maxFtes;
    const updateGroup = useCallback((updatedGroup)=>{
        onChange({
            ...config,
            group: updatedGroup
        });
    }, [
        config,
        onChange
    ]);
    const handleAddLine = useCallback((laborCategoryId, title)=>{
        const newLine = {
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            laborCategoryId,
            label: title,
            parameters: {
                hours: mkParam({
                    name: 'hours',
                    label: 'Hours',
                    paramType: 'quantity',
                    source: 'contractor'
                }),
                rate: mkParam({
                    name: 'rate',
                    label: 'Rate',
                    paramType: 'rate',
                    source: 'computed'
                }),
                total: mkParam({
                    name: 'total',
                    label: 'Total',
                    paramType: 'currency',
                    source: 'computed',
                    formula: {
                        type: 'multiply',
                        operands: [
                            'hours',
                            'rate'
                        ]
                    }
                })
            }
        };
        updateGroup({
            ...group,
            lines: [
                ...group.lines,
                newLine
            ]
        });
        setShowAddRole(false);
    }, [
        group,
        updateGroup
    ]);
    const otherTiers = useMemo(()=>(allSharedTierConfigs ?? []).filter((c)=>c.id !== config.id), [
        allSharedTierConfigs,
        config.id
    ]);
    const handleCopyFrom = useCallback((sourceId)=>{
        const source = otherTiers.find((t)=>t.id === sourceId);
        if (!source) return;
        // Deep-clone source lines into this tier, preserving this tier's label/number/constraints
        const clonedLines = structuredClone(source.group.lines).map((line)=>({
                ...line,
                id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
            }));
        updateGroup({
            ...group,
            lines: clonedLines
        });
        setShowCopyFrom(false);
    }, [
        otherTiers,
        group,
        updateGroup
    ]);
    const handleRemoveLine = useCallback((lineId)=>{
        updateGroup({
            ...group,
            lines: group.lines.filter((l)=>l.id !== lineId)
        });
    }, [
        group,
        updateGroup
    ]);
    const handleFteChange = useCallback((lineId, fteCount)=>{
        updateGroup({
            ...group,
            lines: group.lines.map((l)=>l.id === lineId ? {
                    ...l,
                    fteCount
                } : l)
        });
    }, [
        group,
        updateGroup
    ]);
    const handleParamChange = useCallback((lineId, paramName, value, period)=>{
        updateGroup({
            ...group,
            lines: group.lines.map((l)=>{
                if (l.id !== lineId) return l;
                const param = l.parameters[paramName];
                if (!param) return l;
                let updatedParam;
                if (period && param.perPeriod) {
                    updatedParam = {
                        ...param,
                        periodValues: {
                            ...param.periodValues,
                            [period]: value ?? 0
                        }
                    };
                } else {
                    updatedParam = {
                        ...param,
                        value
                    };
                }
                return {
                    ...l,
                    parameters: {
                        ...l.parameters,
                        [paramName]: updatedParam
                    }
                };
            })
        });
    }, [
        group,
        updateGroup
    ]);
    // Build header columns (non-period for shared tier editor)
    const headerCols = useMemo(()=>{
        const cols = [];
        for (const col of columns){
            cols.push({
                key: col.key,
                label: col.label
            });
        }
        cols.push({
            key: '_actions',
            label: ''
        });
        return cols;
    }, [
        columns
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 flex-wrap",
                children: [
                    /*#__PURE__*/ _jsxs("h2", {
                        className: "text-lg font-semibold text-gray-900",
                        children: [
                            config.tierLabel,
                            " (Tier ",
                            config.tierNumber,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Badge, {
                        variant: "outline",
                        className: "text-xs bg-blue-50 text-blue-700 border-blue-200",
                        children: [
                            "Used by ",
                            referencingClins.length,
                            " CLINs"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: `text-sm ${fteWarning ? 'text-red-500 font-medium' : 'text-gray-400'}`,
                        children: [
                            currentFtes,
                            " FTEs",
                            (minFtes != null || maxFtes != null) && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    ' ',
                                    "/",
                                    ' ',
                                    minFtes != null && maxFtes != null && minFtes === maxFtes ? `${minFtes} required` : `${minFtes ?? '?'}–${maxFtes ?? '?'}`
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-gray-400",
                        children: [
                            lines.length,
                            " ",
                            lines.length === 1 ? 'role' : 'roles'
                        ]
                    }),
                    otherTiers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "relative",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>setShowCopyFrom(!showCopyFrom),
                                className: "text-xs text-blue-600 hover:text-blue-700 px-2 py-0.5 border border-blue-200 rounded",
                                children: "Copy From..."
                            }),
                            showCopyFrom && /*#__PURE__*/ _jsx("div", {
                                className: "absolute top-7 left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px]",
                                children: otherTiers.map((tier)=>/*#__PURE__*/ _jsxs("button", {
                                        onClick: ()=>handleCopyFrom(tier.id),
                                        className: "w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50",
                                        children: [
                                            tier.tierLabel,
                                            " (",
                                            tier.group.lines.length,
                                            " roles)"
                                        ]
                                    }, tier.id))
                            })
                        ]
                    })
                ]
            }),
            referencingClins.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-400",
                children: [
                    "CLINs: ",
                    referencingClins.join(', ')
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full border-collapse",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsx("tr", {
                                className: "border-b-2 border-gray-200",
                                children: headerCols.map((h)=>/*#__PURE__*/ _jsx("th", {
                                        className: `px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider ${h.key.startsWith('label') ? 'text-left' : 'text-right'} ${h.key === '_actions' ? 'w-8' : ''}`,
                                        children: h.label
                                    }, h.key))
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: lines.map((line)=>/*#__PURE__*/ _jsxs("tr", {
                                    className: "border-b border-gray-100 hover:bg-gray-50/50 group",
                                    children: [
                                        columns.map((col)=>{
                                            if (col.key === 'label') {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-3 py-2 text-sm font-medium text-gray-900",
                                                    children: line.label
                                                }, col.key);
                                            }
                                            if (col.key === 'ftes') {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-1",
                                                    children: /*#__PURE__*/ _jsx(InlineFteEditor, {
                                                        value: line.fteCount,
                                                        onChange: (val)=>handleFteChange(line.id, val)
                                                    })
                                                }, col.key);
                                            }
                                            const param = line.parameters[col.parameterName];
                                            if (!param) {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-1 text-sm text-gray-400 text-right",
                                                    children: "—"
                                                }, col.key);
                                            }
                                            return /*#__PURE__*/ _jsx("td", {
                                                className: "px-1 py-1",
                                                children: /*#__PURE__*/ _jsx(ParameterCell, {
                                                    param: param,
                                                    editable: col.editable,
                                                    onChange: (val)=>handleParamChange(line.id, col.parameterName, val)
                                                })
                                            }, col.key);
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-1 py-1 text-center",
                                            children: /*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>handleRemoveLine(line.id),
                                                className: "text-gray-300 hover:text-red-500 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                                                children: /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "w-3.5 h-3.5"
                                                })
                                            })
                                        })
                                    ]
                                }, line.id))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        onClick: ()=>setShowAddRole(!showAddRole),
                        disabled: availableCategories.length === 0,
                        className: "flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "w-3.5 h-3.5"
                            }),
                            "Add Role"
                        ]
                    }),
                    showAddRole && availableCategories.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-6 left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-auto min-w-[250px]",
                        children: availableCategories.map((cat)=>/*#__PURE__*/ _jsxs("button", {
                                onClick: ()=>handleAddLine(cat.laborCategoryId, cat.title),
                                className: "w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50",
                                children: [
                                    cat.title,
                                    cat.level && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-gray-400 ml-1",
                                        children: [
                                            "(",
                                            cat.level,
                                            ")"
                                        ]
                                    })
                                ]
                            }, cat.laborCategoryId))
                    })
                ]
            })
        ]
    });
});
